/**
 * StudyCard Component
 * Card view for a study in the list with Team | Bookmarks | Contacts layout
 */

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Users, ExternalLink, Globe, ChevronDown, Mail, Phone, FileText, Loader2, Archive } from 'lucide-react';
import type { Protocol, StudyBookmark } from '../types';
import { StatusBadge } from './StatusBadge';
import { StarButton } from './StarButton';
import api from '../../services/api';

interface StudyCardProps {
  protocol: Protocol;
  bookmarks?: StudyBookmark[];
  isStarred: boolean;
  onToggleStar: () => Promise<void>;
}

interface TeamMember {
  id: string;
  user_id: string;
  role: string;
  blinding_status: string | null;
  user: {
    id: string;
    email: string;
    full_name: string;
  } | null;
}

interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
  email: string | null;
  phone: string | null;
  company: string | null;
}

// Reorder items for column-first display in a row-major grid
function reorderForColumnFirst<T>(items: T[], numCols: number): T[] {
  if (items.length === 0) return items;
  const rows = Math.ceil(items.length / numCols);
  const result: T[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < numCols; col++) {
      const index = col * rows + row;
      if (index < items.length) {
        result.push(items[index]);
      }
    }
  }
  return result;
}

// Priority roles that should be shown prominently
const PRIORITY_ROLES = ['Principal Investigator', 'Lead Coordinator'];

export function StudyCard({ protocol, bookmarks = [], isStarred, onToggleStar }: StudyCardProps) {
  const [showOtherTeamMembers, setShowOtherTeamMembers] = useState(false);
  const [isLoadingProtocol, setIsLoadingProtocol] = useState(false);
  const [protocolError, setProtocolError] = useState<string | null>(null);

  const handleViewProtocol = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLoadingProtocol) return;

    setIsLoadingProtocol(true);
    setProtocolError(null);

    try {
      let response = await api.get(
        `/api/intelligence/protocols/${protocol.id}/documents?document_type=protocol`
      );
      let docs = response.data;

      if (!docs || docs.length === 0) {
        response = await api.get(
          `/api/intelligence/protocols/${protocol.id}/documents`
        );
        docs = response.data;
      }

      if (!docs || docs.length === 0) {
        setProtocolError('No documents found');
        setTimeout(() => setProtocolError(null), 4000);
        return;
      }

      const docId = docs[0].id;
      const downloadResponse = await api.get(
        `/api/intelligence/documents/${docId}/download`,
        { responseType: 'blob' }
      );

      const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (err: any) {
      console.error('Error viewing protocol document:', err);
      setProtocolError(err.response?.data?.error || 'Failed to load document');
      setTimeout(() => setProtocolError(null), 4000);
    } finally {
      setIsLoadingProtocol(false);
    }
  }, [protocol.id, isLoadingProtocol]);

  // Always use 3 columns with 5 rows max (15 bookmarks)
  const displayBookmarks = bookmarks.slice(0, 15);
  const numCols = 3;
  const orderedBookmarks = reorderForColumnFirst(displayBookmarks, numCols);

  const teamMembers: TeamMember[] = protocol.team_members || [];
  const contacts: Contact[] = protocol.contacts || [];

  // Separate priority team members (PI, Lead CRC) from others
  const priorityMembers = teamMembers.filter(m =>
    PRIORITY_ROLES.some(role => m.role.includes(role) || m.role.includes('PI') || m.role.includes('CRC'))
  );
  const otherMembers = teamMembers.filter(m =>
    !PRIORITY_ROLES.some(role => m.role.includes(role) || m.role.includes('PI') || m.role.includes('CRC'))
  );

  const isArchived = protocol.status === 'inactive';

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border hover:shadow-md transition-all duration-150 ${
      isArchived
        ? 'border-gray-300 dark:border-gray-600 opacity-60'
        : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
    }`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {/* Sponsor icon/placeholder */}
            <Link to={`/siteorganizer/study/${protocol.id}`} className="flex-shrink-0">
              <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-150">
                <span className="text-gray-500 dark:text-gray-300 text-xs font-medium">
                  {protocol.sponsor_name?.charAt(0)?.toUpperCase() || 'S'}
                </span>
              </div>
            </Link>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  to={`/siteorganizer/study/${protocol.id}`}
                  className="text-sm text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 truncate transition-colors duration-150 hidden sm:inline"
                >
                  {protocol.sponsor_name || 'Unknown Sponsor'}
                </Link>
                <span className="text-gray-300 dark:text-gray-600 hidden sm:inline">|</span>
                <Link
                  to={`/siteorganizer/study/${protocol.id}`}
                  className="text-sm font-semibold text-gray-900 hover:text-primary-600 dark:text-gray-100 dark:hover:text-primary-400 truncate transition-colors duration-150"
                >
                  {protocol.protocol_number}
                </Link>
                {protocol.study_nickname && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600 hidden sm:inline">|</span>
                    <Link
                      to={`/siteorganizer/study/${protocol.id}`}
                      className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 truncate transition-colors duration-150 font-medium hidden sm:inline"
                    >
                      {protocol.study_nickname}
                    </Link>
                  </>
                )}
              </div>
              {protocol.indication && (
                <p className="text-sm text-gray-600 dark:text-gray-300 truncate mt-0.5">
                  {protocol.indication}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Archived badge */}
            {isArchived && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-200 dark:bg-gray-700 dark:text-gray-300 rounded-full">
                <Archive size={12} />
                Archived
              </span>
            )}
            {/* Status and Phase */}
            <div className="flex items-center gap-2">
              <StatusBadge status={protocol.enrollment_status} size="sm" />
              {protocol.study_phase && (
                <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">{protocol.study_phase}</span>
              )}
            </div>
            <StarButton isStarred={isStarred} onToggle={onToggleStar} />
          </div>
        </div>
      </div>

      {/* Main Content: Team | Bookmarks | Contacts */}
      <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-gray-200 dark:divide-gray-700">
        {/* Left: Team Section */}
        <div className="w-full md:w-48 md:flex-shrink-0 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Users size={14} className="text-gray-500 dark:text-gray-400" />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Team</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">({teamMembers.length})</span>
          </div>

          {teamMembers.length === 0 ? (
            <div className="text-xs text-gray-400 dark:text-gray-500 italic">No team assigned</div>
          ) : (
            <div className="space-y-1.5">
              {/* Priority members (PI, Lead CRC) shown prominently */}
              {priorityMembers.map((member) => (
                <div key={member.id} className="flex items-center justify-between group">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                      {member.user?.full_name || 'Unknown'}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{member.role}</div>
                  </div>
                  {member.user?.email && (
                    <a
                      href={`mailto:${member.user.email}`}
                      onClick={(e) => e.stopPropagation()}
                      className="p-1 text-gray-300 hover:text-primary-600 dark:text-gray-600 dark:hover:text-primary-400 transition-colors duration-150 opacity-0 group-hover:opacity-100"
                      title={member.user.email}
                    >
                      <Mail size={12} />
                    </a>
                  )}
                </div>
              ))}

              {/* Dropdown for other team members */}
              {otherMembers.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setShowOtherTeamMembers(!showOtherTeamMembers)}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-150"
                  >
                    <ChevronDown size={12} className={`transition-transform ${showOtherTeamMembers ? 'rotate-180' : ''}`} />
                    <span>{otherMembers.length} more</span>
                  </button>
                  {showOtherTeamMembers && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowOtherTeamMembers(false)} />
                      <div className="absolute left-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1 max-h-48 overflow-y-auto dark:bg-gray-800 dark:border-gray-700">
                        {otherMembers.map((member) => (
                          <div key={member.id} className="px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                                {member.user?.full_name || 'Unknown'}
                              </div>
                              <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{member.role}</div>
                            </div>
                            {member.user?.email && (
                              <a
                                href={`mailto:${member.user.email}`}
                                onClick={(e) => e.stopPropagation()}
                                className="p-1 text-gray-400 hover:text-primary-600 dark:text-gray-500 dark:hover:text-primary-400 transition-colors duration-150"
                                title={member.user.email}
                              >
                                <Mail size={12} />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* If no priority members, show first few team members */}
              {priorityMembers.length === 0 && otherMembers.length === 0 && teamMembers.slice(0, 2).map((member) => (
                <div key={member.id} className="flex items-center justify-between group">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                      {member.user?.full_name || 'Unknown'}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{member.role}</div>
                  </div>
                  {member.user?.email && (
                    <a
                      href={`mailto:${member.user.email}`}
                      onClick={(e) => e.stopPropagation()}
                      className="p-1 text-gray-300 hover:text-primary-600 dark:text-gray-600 dark:hover:text-primary-400 transition-colors duration-150 opacity-0 group-hover:opacity-100"
                      title={member.user.email}
                    >
                      <Mail size={12} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Middle: Bookmarks Section */}
        <div className="flex-1 p-3 min-w-0">
          {displayBookmarks.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {orderedBookmarks.map((bookmark) => (
                <a
                  key={bookmark.id}
                  href={bookmark.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 hover:bg-primary-50 hover:border-primary-200 dark:bg-gray-700/50 dark:hover:bg-primary-900/20 dark:hover:border-primary-800 border border-gray-200 dark:border-gray-700 rounded-md text-xs transition-colors duration-150 group"
                  title={bookmark.url}
                >
                  {/* Favicon */}
                  {bookmark.favicon_url ? (
                    <img
                      src={bookmark.favicon_url}
                      alt=""
                      className="w-4 h-4 flex-shrink-0"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <Globe
                    size={14}
                    className={`text-gray-400 dark:text-gray-500 flex-shrink-0 ${bookmark.favicon_url ? 'hidden' : ''}`}
                  />
                  {/* Type badge */}
                  <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded text-[10px] font-medium uppercase flex-shrink-0">
                    {bookmark.bookmark_type}
                  </span>
                  {/* Label */}
                  <span className="text-gray-700 group-hover:text-primary-700 dark:text-gray-200 dark:group-hover:text-primary-300 truncate sm:max-w-[100px]">
                    {bookmark.label}
                  </span>
                  <ExternalLink size={10} className="text-gray-400 group-hover:text-primary-500 dark:text-gray-500 dark:group-hover:text-primary-400 flex-shrink-0" />
                </a>
              ))}
              {(protocol.bookmark_count || 0) > 15 && (
                <Link
                  to={`/siteorganizer/study/${protocol.id}`}
                  className="inline-flex items-center justify-center px-2.5 py-1.5 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  +{(protocol.bookmark_count || 0) - 15} more
                </Link>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-xs text-gray-400 dark:text-gray-500 italic">
              No bookmarks
            </div>
          )}
        </div>

        {/* Right: Contacts Section (openly listed, no dropdown) */}
        <div className="w-full md:w-56 md:flex-shrink-0 p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Phone size={14} className="text-gray-500 dark:text-gray-400" />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Contacts</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">({contacts.length})</span>
          </div>

          {contacts.length === 0 ? (
            <div className="text-xs text-gray-400 dark:text-gray-500 italic">No contacts</div>
          ) : (
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {contacts.map((contact) => (
                <div key={contact.id} className="flex items-center justify-between group">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                      {contact.first_name} {contact.last_name}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                      {contact.role}
                      {contact.company && <span className="text-gray-400 dark:text-gray-500"> · {contact.company}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {contact.email && (
                      <a
                        href={`mailto:${contact.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="p-1 text-gray-400 hover:text-primary-600 dark:text-gray-500 dark:hover:text-primary-400 transition-colors duration-150"
                        title={contact.email}
                      >
                        <Mail size={12} />
                      </a>
                    )}
                    {contact.phone && (
                      <a
                        href={`tel:${contact.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="p-1 text-gray-400 hover:text-green-600 dark:text-gray-500 dark:hover:text-green-400 transition-colors duration-150"
                        title={contact.phone}
                      >
                        <Phone size={12} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/40 rounded-b-xl border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>{protocol.site_name || 'Site'}</span>
          <div className="flex items-center gap-3">
            <button
              onClick={handleViewProtocol}
              disabled={isLoadingProtocol}
              className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium disabled:opacity-50"
            >
              {isLoadingProtocol ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <FileText size={12} />
              )}
              <span className="hidden sm:inline">View Protocol</span>
            </button>
            <Link
              to={`/siteorganizer/study/${protocol.id}`}
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
            >
              View Details →
            </Link>
          </div>
        </div>
        {protocolError && (
          <div className="mt-1 text-xs text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20 px-2 py-1 rounded">
            {protocolError}
          </div>
        )}
      </div>
    </div>
  );
}

export default StudyCard;
